// Thin browser-only wiring that turns score-sheet models into a print dialog.
//
// The printable document (see `scoreSheetDocument.ts`) is written into a hidden
// off-screen iframe and printed from there. An iframe (rather than a popup
// window) keeps the print self-contained, avoids popup blockers, and never
// disturbs the visible app.

import type {
  BracketScoreSheet,
  GroupScoreSheet,
  RefereeMatchForm,
} from '../domain';
import { DEFAULT_LOCALE, type Locale } from '../i18n/messages';
import {
  renderBracketSheetHtml,
  renderScoreSheetHtml,
} from './scoreSheetDocument';
import { renderRefereeFormHtml } from './refereeFormDocument';

/**
 * Opens the browser print dialog for the given group score sheets, rendered in
 * `locale` (English when omitted). Renders the document into an off-screen
 * iframe, prints it once loaded, then removes the iframe. A no-op when there
 * is nothing to print or the DOM is unavailable.
 */
export function printScoreSheets(
  sheets: readonly GroupScoreSheet[],
  locale: Locale = DEFAULT_LOCALE,
): void {
  if (sheets.length === 0) return;
  printHtmlDocument(renderScoreSheetHtml(sheets, locale));
}

/**
 * Opens the browser print dialog for the given bracket match score sheets —
 * the playoff counterpart of `printScoreSheets`, sharing the same off-screen
 * iframe mechanics. A no-op when there is nothing to print or the DOM is
 * unavailable.
 */
export function printBracketScoreSheets(
  sheets: readonly BracketScoreSheet[],
  locale: Locale = DEFAULT_LOCALE,
): void {
  if (sheets.length === 0) return;
  printHtmlDocument(renderBracketSheetHtml(sheets, locale));
}

/**
 * Opens the browser print dialog for the given football referee match-report
 * forms (one A4 page per match), sharing the same off-screen iframe mechanics.
 * A no-op when there is nothing to print or the DOM is unavailable.
 */
export function printRefereeForms(
  forms: readonly RefereeMatchForm[],
  locale: Locale = DEFAULT_LOCALE,
): void {
  if (forms.length === 0) return;
  printHtmlDocument(renderRefereeFormHtml(forms, locale));
}

/** Renders `html` into a hidden iframe, prints it, then removes the iframe. */
function printHtmlDocument(html: string): void {
  if (typeof document === 'undefined') return;

  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';

  let cleaned = false;
  const cleanup = () => {
    if (cleaned) return;
    cleaned = true;
    iframe.remove();
  };

  iframe.onload = () => {
    const frameWindow = iframe.contentWindow;
    if (!frameWindow) {
      cleanup();
      return;
    }
    // Tear down once printing finishes (or is cancelled). `onafterprint` covers
    // browsers where `print()` blocks (Chrome) and those where it returns
    // immediately (Firefox). As a fallback, when focus returns to the main
    // window after the dialog closes, clean up shortly after — this avoids a
    // fixed timer that could remove the iframe while the dialog is still open.
    frameWindow.onafterprint = cleanup;
    window.addEventListener(
      'focus',
      () => window.setTimeout(cleanup, 1_000),
      { once: true },
    );
    frameWindow.focus();
    frameWindow.print();
  };

  // Assign `srcdoc` and the handler before inserting the iframe so the only
  // document that ever loads is the score sheet (never the initial about:blank).
  iframe.srcdoc = html;
  document.body.appendChild(iframe);
}
